/**
 * THE CONTEXT BAR — the one place context is chosen. 09 §3.
 *
 * Binding layout rules implemented here (09 §3.2):
 *   • Sticky at the top of the viewport, full width, one line on desktop.
 *   • Right-anchored. The breadcrumb BEGINS at the right edge with הארגון and proceeds leftwards:
 *       הארגון ‹ החברה ‹ המודול ‹ הישות ‹ התקופה
 *   • The separator is `‹` — never `>` and never `/`. In RTL flow it points forward, i.e. leftwards.
 *   • The period selector and back-to-parent sit together and never move between screens.
 *   • Global utilities sit at the LEFT end — the RTL equivalent of "far corner".
 *   • A slot that does not apply is ABSENT, never `—`, never a greyed dropdown (09 §3.3).
 *
 * This is the only component in the app that calls `useContextSelection`, and therefore the only
 * component that can change the company or the period. `contextSingleSource.test.ts` enforces it.
 */

import { useCallback, useState } from 'react';

import { useContextSelection, useWorkContext } from './ContextProvider';
import { callOperation } from '../api/operations';
import { SurfaceBadge } from './SurfaceBadge';
import { LifecycleChip } from '../components/LifecycleChip';
import { useLocale } from '../i18n/LocaleProvider';

/** 09 §3.2: the separator, stated once. */
const CHEVRON = '‹';

/*
 * `backToParent` IS GONE FROM HERE. **Section 1, 2026-10-27.**
 *
 * It was drawn inside `.context-bar__controls`, beside the period selector, so the button sat
 * in a different place on every screen — the selector is ABSENT on a screen that is not
 * period-bound (09 s5.2), and the breadcrumb beside it is a different width on each. Owner:
 * *"כותרות ופעולות מחליפות מיקום"*, and answer 2 of the same day: *"במיקום ובגובה קבועים,
 * בנפרד מאזור הלשוניות"*.
 *
 * `AreaBar` owns it now. The prop was REMOVED rather than kept and ignored: a prop two
 * components accept and one draws is how a control ends up rendered twice.
 */

export function ContextBar(): JSX.Element {
  const ctx = useWorkContext();
  const selection = useContextSelection('ContextBar');
  // The toolbar is chrome and follows the chosen language, which the rail beside it already did.
  const { t } = useLocale();
  const [signingOut, setSigningOut] = useState(false);
  /**
   * The reference code of a sign-out that did not happen, or `null`.
   *
   * `''` is a real value here and means "it failed and the server gave us no reference" — a network
   * error, say. `null` is "nothing has failed". The two are not the same and a falsy check would
   * merge them, which is how a failure becomes invisible.
   */
  const [signOutFailure, setSignOutFailure] = useState<string | null>(null);

  /**
   * End THIS session.
   *
   * The server clears the cookie and revokes the row; the browser then reloads to `/` rather than
   * routing, because the whole point is to discard every piece of in-memory state a signed-in shell
   * was holding. A client-side navigation would keep the old `whoami` answer in React state and show
   * a signed-in shell to somebody who has just signed out.
   *
   * A failure leaves the button enabled and does NOT pretend: a sign-out that reported success while
   * the session lived on is the worst possible lie for this particular control.
   *
   * ---- AND IT NO LONGER FAILS SILENTLY -------------------------------------------------------
   *
   * `catch { setSigningOut(false) }` swallowed the refusal whole. On the live Pilot the request was
   * rejected before it reached a handler -- the app was not sending the CSRF header -- and the
   * button simply went back to saying יציאה. The Owner pressed it, nothing happened, and the
   * product had no way to say so.
   *
   * The transport defect is fixed at the choke point in `api/client.ts`. This is the other half:
   * whatever the reason, a sign-out that did not happen is now VISIBLE. The user is told the
   * session is still open and given the short reference code -- never a route, a status, an
   * exception type or anything about why the server said no (09 §9.1 V3).
   */
  const signOut = useCallback(async (): Promise<void> => {
    setSigningOut(true);
    setSignOutFailure(null);
    try {
      await callOperation('auth.session.delete', { body: {} });
      window.location.assign('/');
    } catch (error: unknown) {
      // `requestUid` is the only field of any error this component is allowed to read.
      const uid =
        typeof error === 'object' && error !== null && 'requestUid' in error
          ? String((error as { requestUid: unknown }).requestUid)
          : '';
      setSignOutFailure(uid);
      setSigningOut(false);
    }
  }, []);

  /*
   * ── THE ORGANISATION IS ALWAYS NAMED; THE PICKER IS WHAT DEPENDS ON HAVING TWO ────────────────
   *
   * 09 §3.1 says the ארגון SLOT is shown only when the person belongs to more than one tenant, and
   * that was read as "say nothing about the office when there is only one" -- so the single-office
   * case, which is every customer today, saw no organisation name anywhere in the bar.
   *
   * Owner, section 1, 2026-10-28: *"שם הארגון, המשתמש והחברה שנבחרה צריכים להיות בסרגל העליון,
   * באזור שבו מופיע „סביבת הארגון", עם הבחנה ברורה ביניהם."*
   *
   * The rule §3.1 is actually about is CHOICE: a picker with one option is a control that cannot
   * do anything. So one office is NAMED, several are CHOSEN, and the label is the same either way.
   */
  const canChooseTenant = (ctx.session?.tenants.length ?? 0) > 1;

  return (
    <header className="context-bar" data-surface={ctx.surface ?? 'UNREAD'}>
      {/* Right-anchored breadcrumb: reading starts at the right edge. */}
      <nav className="context-bar__chain" aria-label={t('context.chain')}>
        <SurfaceBadge surface={ctx.surface} supportGrantUid={ctx.session?.supportGrantUid ?? null} />

        {ctx.tenant === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            {canChooseTenant ? (
              <label className="context-bar__slot">
                <span className="context-bar__slot-label">{t('context.tenant')}</span>
                <select
                  className="context-bar__control"
                  value={ctx.tenant.tenantUid}
                  onChange={(e) => selection.selectTenant(e.currentTarget.value)}
                >
                  {ctx.session?.tenants.map((t) => (
                    <option key={t.tenantUid} value={t.tenantUid}>
                      {t.displayName}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className="context-bar__slot">
                <span className="context-bar__slot-label">{t('context.tenant')}</span>
                <span className="context-bar__value">{ctx.tenant.displayName}</span>
              </span>
            )}
          </>
        )}

        {/*
          THE SIGNED-IN PERSON, IN THE SAME ROW AS THE OTHER TWO. It was in the toolbar at the
          other end of the header, which is why the home page grew a block repeating all three.
          One row, three labels, one reading.
        */}
        {ctx.session === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <span className="context-bar__slot">
              <span className="context-bar__slot-label">{t('context.user')}</span>
              <span className="context-bar__value">{ctx.session.displayName}</span>
            </span>
          </>
        )}

        {/*
          THE company picker. There is exactly one instance of this control in the application.
          09 §5.1: the list is the server's single "visible companies" answer, and every row shows an
          explicit lifecycle state — a company's state is displayed, never inferred.
          Absent (not greyed) on a platform screen with no company (09 §3.3).
        */}
{/*
          ── ONE, SEVERAL, OR NONE — THREE SHAPES. **Owner section 3, 2026-10-28.** ────────────

              *"כשיש למשתמש חברה נגישה אחת בלבד, היא תיבחר אוטומטית ושמה יוצג ללא בורר מיותר.
              כשיש כמה חברות נגישות, יוצג בורר. הבורר יציג רק חברות שהמשתמש מורשה אליהן."*

          THERE IS STILL EXACTLY ONE COMPANY PICKER IN THE APPLICATION. 09 §5.1 and §3.4 are
          unchanged; what changes is that a control offering a single option is not a choice, it is
          a question with one answer, and the provider has already answered it.

          `ctx.visibleCompanies` is the server's own grant-filtered answer (`companies.list.read`),
          so *"רק חברות שהמשתמש מורשה אליהן"* is a property of the list and not of this markup.

          NONE renders NOTHING, for the reason a platform screen renders nothing (09 §3.3): an
          empty dropdown is a control that cannot be used, and there is no wording here to invent
          for it — `חברות שלי` is the screen that explains an empty list.
        */}
        {ctx.surface === 'TENANT' && ctx.visibleCompanies.length > 0 ? (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            {ctx.visibleCompanies.length === 1 ? (
              <span className="context-bar__slot">
                <span className="context-bar__slot-label">{t('context.company')}</span>
                {/* 09 §9.1 V1: the full legal name. Never a db code, never a ח.פ as the identity. */}
                <span className="context-bar__value">{ctx.visibleCompanies[0]?.legalName}</span>
                {ctx.company === null ? null : <LifecycleChip status={ctx.company.lifecycleStatus} />}
              </span>
            ) : (
              <label className="context-bar__slot">
                <span className="context-bar__slot-label">{t('context.company')}</span>
                <select
                  className="context-bar__control context-bar__control--company"
                  value={ctx.company?.companyUid ?? ''}
                  onChange={(e) => selection.selectCompany(e.currentTarget.value)}
                >
                  <option value="" disabled>
                    {t('context.chooseCompany')}
                  </option>
                  {ctx.visibleCompanies.map((c) => (
                    <option key={c.companyUid} value={c.companyUid}>
                      {c.legalName}
                    </option>
                  ))}
                </select>
                {ctx.company === null ? null : <LifecycleChip status={ctx.company.lifecycleStatus} />}
              </label>
            )}
          </>
        ) : null}

        {ctx.module === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <span className="context-bar__slot">
              <span className="context-bar__slot-label">{t('context.module')}</span>
              <span className="context-bar__value">{ctx.module.displayNameHe}</span>
            </span>
          </>
        )}

        {ctx.entityLabelHe === null ? null : (
          <>
            <span className="context-bar__sep" aria-hidden="true">
              {CHEVRON}
            </span>
            <span className="context-bar__slot">
              <span className="context-bar__slot-label">{t('context.entity')}</span>
              {/* 09 §3.2: the middle truncates, never the current screen name. */}
              <span className="context-bar__value context-bar__value--current">{ctx.entityLabelHe}</span>
            </span>
          </>
        )}
      </nav>

      {/*
        The two controls the user reaches for constantly. They sit together and never move.
        09 §5.2: the period is ABSENT (not greyed) on a screen that is not period-bound.
      */}
      <div className="context-bar__controls">
        {ctx.periods.length === 0 ? null : (
          <label className="context-bar__slot">
            <span className="context-bar__slot-label">{t('context.period')}</span>
            <select
              className="context-bar__control"
              value={ctx.period?.periodUid ?? ''}
              onChange={(e) => selection.selectPeriod(e.currentTarget.value || null)}
            >
              <option value="">{t('context.allPeriods')}</option>
              {ctx.periods.map((p) => (
                <option key={p.periodUid} value={p.periodUid}>
                  {/* 09 §5.2: the resolved business form, never the raw source key. */}
                  {p.displayHe}
                </option>
              ))}
            </select>
            {ctx.period?.locked === true ? (
              <span className="chip chip--lock">{t('context.locked')}</span>
            ) : null}
          </label>
        )}

      </div>

      {/* 09 §3.2: global utilities at the LEFT end — the RTL far corner. */}
      <div className="context-bar__utilities">
        <a className="context-bar__utility" href="#search">
          {t('shell.search')}
        </a>
        <a className="context-bar__utility" href="#documents">
          {t('shell.documents')}
        </a>
        {/*
          CM-014: THE SURFACE SWITCH IS GONE, and its absence is the correction. It set a piece of
          React state, so a person on the tenant surface could put themselves on the platform
          surface by clicking. OA-0001 §2.1 withdrew the two-context identity: the surface comes
          from the session the server states, and there is nothing here that could change it.
        */}

        {/*
          SIGN OUT. **G-07's gap, open since Phase 1.**

          `auth.session.delete` was recorded CLOSED in the ledger on the strength of the route being
          declared and `SessionService.signOut` existing — and no control in this app posted to it.
          CM-023 struck that row; this is the control it was struck for.

          It is deliberately NOT "sign out everywhere": that is `user.session.revoke_all`, a
          different action with a different blast radius, and it lives on the personal-security
          screen where somebody choosing it can read what it does first.
        */}
        <button
          type="button"
          className="context-bar__utility"
          disabled={signingOut}
          onClick={() => void signOut()}
        >
          {signingOut ? t('shell.signingOut') : t('shell.signOut')}
        </button>

        {/*
          AND WHEN IT DID NOT WORK, SAY SO.

          `role="alert"` so a screen reader is told without the user having to go looking, and the
          text says the one thing that matters: YOU ARE STILL SIGNED IN. A control that fails
          quietly on this particular action leaves somebody walking away from a live session
          believing they closed it.
        */}
        {signOutFailure !== null && (
          <span className="context-bar__utility-error" role="alert">
            {t('shell.signOutFailed')}
            {signOutFailure === '' ? '' : ` (${signOutFailure})`}
          </span>
        )}
      </div>
    </header>
  );
}
